import { useState, useEffect, useRef, useCallback } from 'react';

// --- Types ---

export type QualityStatus = 'green' | 'yellow' | 'red';

export interface QualityLevel {
  status: QualityStatus;
  value: number;
  message: string;
}

export interface QualityMetrics {
  noise: QualityLevel;
  multiSpeaker: QualityLevel;
  bitDepth: QualityLevel;
  sampleRate: QualityLevel;
  speechActivity: QualityLevel;
  overallStatus: QualityStatus;
}

// --- Constants ---

/** Sliding window duration for noise RMS calculation (seconds) */
const NOISE_WINDOW_SECONDS = 2;

/** RMS thresholds in dBFS */
const NOISE_THRESHOLD_RED = -30;
const NOISE_THRESHOLD_YELLOW = -35;

/** Sample rate minimum (Hz) */
const MIN_SAMPLE_RATE = 16000;

/** Bit depth minimum */
const MIN_BIT_DEPTH = 16;

/** Speech activity: minimum amplitude threshold to count as speech */
const SPEECH_AMPLITUDE_THRESHOLD = 0.02;

/** Speech activity: minimum percentage of frames with speech for green */
const SPEECH_ACTIVITY_GREEN = 0.7;
/** Speech activity: minimum percentage for yellow */
const SPEECH_ACTIVITY_YELLOW = 0.4;

/** Spectral centroid variance threshold for multi-speaker detection */
const SPECTRAL_CENTROID_VARIANCE_THRESHOLD = 5000;

// --- Utility Functions (exported for testing) ---

/**
 * Calculate RMS (Root Mean Square) of a Float32Array signal buffer.
 */
export function calculateRMS(buffer: Float32Array): number {
  if (buffer.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < buffer.length; i++) {
    sum += buffer[i] * buffer[i];
  }
  return Math.sqrt(sum / buffer.length);
}

/**
 * Estimate the noise floor by calculating RMS over small chunks (e.g., 50ms)
 * and returning the minimum RMS found. This prevents speech peaks from
 * being interpreted as background noise.
 */
export function calculateNoiseFloor(buffer: Float32Array, sampleRate: number): number {
  if (buffer.length === 0) return 0;
  const chunkSize = Math.floor(sampleRate * 0.05); // 50ms chunks
  if (chunkSize === 0 || buffer.length < chunkSize) return calculateRMS(buffer);

  let minRms = Infinity;
  for (let i = 0; i <= buffer.length - chunkSize; i += chunkSize) {
    const chunk = buffer.subarray(i, i + chunkSize);
    const rms = calculateRMS(chunk);
    if (rms < minRms) {
      minRms = rms;
    }
  }
  
  return minRms === Infinity ? calculateRMS(buffer) : minRms;
}

/**
 * Convert linear RMS amplitude to dBFS (decibels relative to full scale).
 * Full scale = 1.0 in floating point audio.
 */
export function rmsToDbfs(rms: number): number {
  if (rms <= 0) return -Infinity;
  return 20 * Math.log10(rms);
}

/**
 * Evaluate noise level from dBFS value.
 */
export function evaluateNoiseLevel(dbfs: number): QualityLevel {
  if (dbfs > NOISE_THRESHOLD_RED) {
    return {
      status: 'red',
      value: dbfs,
      message: `High background noise: ${dbfs.toFixed(1)} dBFS (exceeds -30 dBFS)`,
    };
  }
  if (dbfs > NOISE_THRESHOLD_YELLOW) {
    return {
      status: 'yellow',
      value: dbfs,
      message: `Moderate background noise: ${dbfs.toFixed(1)} dBFS`,
    };
  }
  return {
    status: 'green',
    value: dbfs,
    message: `Noise level acceptable: ${dbfs.toFixed(1)} dBFS`,
  };
}

/**
 * Calculate spectral centroid from frequency data.
 * The spectral centroid is the "center of mass" of the spectrum.
 */
export function calculateSpectralCentroid(
  frequencyData: Uint8Array,
  sampleRate: number,
  fftSize: number
): number {
  let weightedSum = 0;
  let magnitudeSum = 0;
  const binWidth = sampleRate / fftSize;

  for (let i = 0; i < frequencyData.length; i++) {
    const magnitude = frequencyData[i];
    const frequency = i * binWidth;
    weightedSum += magnitude * frequency;
    magnitudeSum += magnitude;
  }

  if (magnitudeSum === 0) return 0;
  return weightedSum / magnitudeSum;
}

/**
 * Evaluate multi-speaker detection based on spectral centroid variance.
 */
export function evaluateMultiSpeaker(centroidVariance: number): QualityLevel {
  if (centroidVariance > SPECTRAL_CENTROID_VARIANCE_THRESHOLD) {
    return {
      status: 'red',
      value: centroidVariance,
      message: 'Possible overlapping voices or disruptive noise detected',
    };
  }
  if (centroidVariance > SPECTRAL_CENTROID_VARIANCE_THRESHOLD * 0.7) {
    return {
      status: 'yellow',
      value: centroidVariance,
      message: 'Possible overlapping audio detected',
    };
  }
  return {
    status: 'green',
    value: centroidVariance,
    message: 'No overlapping audio detected',
  };
}

/**
 * Evaluate sample rate quality.
 */
export function evaluateSampleRate(rate: number): QualityLevel {
  if (rate >= MIN_SAMPLE_RATE) {
    return {
      status: 'green',
      value: rate,
      message: `Sample rate: ${rate} Hz (meets minimum ${MIN_SAMPLE_RATE} Hz)`,
    };
  }
  return {
    status: 'red',
    value: rate,
    message: `Sample rate: ${rate} Hz (below minimum ${MIN_SAMPLE_RATE} Hz)`,
  };
}

/**
 * Evaluate bit depth quality.
 */
export function evaluateBitDepth(depth: number): QualityLevel {
  if (depth >= MIN_BIT_DEPTH) {
    return {
      status: 'green',
      value: depth,
      message: `Bit depth: ${depth}-bit (meets minimum ${MIN_BIT_DEPTH}-bit)`,
    };
  }
  if (depth >= 12) {
    return {
      status: 'yellow',
      value: depth,
      message: `Bit depth: ${depth}-bit (below recommended ${MIN_BIT_DEPTH}-bit)`,
    };
  }
  return {
    status: 'red',
    value: depth,
    message: `Bit depth: ${depth}-bit (below minimum 12-bit)`,
  };
}

/**
 * Evaluate speech activity level.
 * @param activityRatio - Fraction of frames with speech (0-1)
 */
export function evaluateSpeechActivity(activityRatio: number): QualityLevel {
  if (activityRatio >= SPEECH_ACTIVITY_GREEN) {
    return {
      status: 'green',
      value: activityRatio,
      message: `Speech activity: ${(activityRatio * 100).toFixed(0)}% (good)`,
    };
  }
  if (activityRatio >= SPEECH_ACTIVITY_YELLOW) {
    return {
      status: 'yellow',
      value: activityRatio,
      message: `Speech activity: ${(activityRatio * 100).toFixed(0)}% (low clarity)`,
    };
  }
  return {
    status: 'red',
    value: activityRatio,
    message: `Speech activity: ${(activityRatio * 100).toFixed(0)}% (insufficient speech detected)`,
  };
}

/**
 * Compute the overall status from individual quality levels.
 * Red if any metric is red, yellow if any is yellow, green otherwise.
 */
export function computeOverallStatus(metrics: Omit<QualityMetrics, 'overallStatus'>): QualityStatus {
  const statuses = [
    metrics.noise.status,
    metrics.multiSpeaker.status,
    metrics.bitDepth.status,
    metrics.sampleRate.status,
    metrics.speechActivity.status,
  ];

  if (statuses.includes('red')) return 'red';
  if (statuses.includes('yellow')) return 'yellow';
  return 'green';
}

// --- Hook ---

const DEFAULT_METRICS: QualityMetrics = {
  noise: { status: 'green', value: -Infinity, message: 'Waiting for audio...' },
  multiSpeaker: { status: 'green', value: 0, message: 'Waiting for audio...' },
  bitDepth: { status: 'green', value: 16, message: 'Waiting for audio...' },
  sampleRate: { status: 'green', value: 0, message: 'Waiting for audio...' },
  speechActivity: { status: 'green', value: 0, message: 'Waiting for audio...' },
  overallStatus: 'green',
};

/**
 * useQualityAnalyzer — Real-time audio quality analysis hook.
 *
 * Accepts a MediaStream (from getUserMedia) and continuously analyzes
 * audio quality metrics using the Web Audio API. Returns color-coded
 * quality indicators updated at < 500ms latency.
 */
export function useQualityAnalyzer(stream: MediaStream | null): QualityMetrics {
  const [metrics, setMetrics] = useState<QualityMetrics>(DEFAULT_METRICS);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Sliding window buffer for noise RMS (time-domain samples)
  const noiseBufferRef = useRef<Float32Array>(new Float32Array(0));
  const noiseBufferIndexRef = useRef(0);

  // Spectral centroid history for multi-speaker variance
  const centroidHistoryRef = useRef<number[]>([]);
  const maxCentroidHistory = 30; // ~30 frames ≈ 0.5-1s of history

  // Speech activity tracking
  const speechFramesRef = useRef(0);
  const totalFramesRef = useRef(0);

  const cleanup = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    noiseBufferRef.current = new Float32Array(0);
    noiseBufferIndexRef.current = 0;
    centroidHistoryRef.current = [];
    speechFramesRef.current = 0;
    totalFramesRef.current = 0;
  }, []);

  useEffect(() => {
    if (!stream) {
      cleanup();
      setMetrics(DEFAULT_METRICS);
      return;
    }

    // Set up Web Audio API pipeline
    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;

    const source = audioContext.createMediaStreamSource(stream);
    sourceRef.current = source;

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.3;
    analyserRef.current = analyser;

    source.connect(analyser);

    // Allocate noise sliding window buffer (2 seconds of samples)
    const bufferSize = Math.ceil(audioContext.sampleRate * NOISE_WINDOW_SECONDS);
    noiseBufferRef.current = new Float32Array(bufferSize);
    noiseBufferIndexRef.current = 0;

    // Evaluate static metrics (bit depth and sample rate)
    const currentSampleRate = audioContext.sampleRate;
    const sampleRateLevel = evaluateSampleRate(currentSampleRate);

    // Attempt to get bit depth from track settings
    const tracks = stream.getAudioTracks();
    let detectedBitDepth = 16; // Default assumption for Web Audio
    if (tracks.length > 0) {
      const settings = tracks[0].getSettings();
      // sampleSize is the bit depth in MediaTrackSettings
      if (settings.sampleSize) {
        detectedBitDepth = settings.sampleSize;
      }
    }
    const bitDepthLevel = evaluateBitDepth(detectedBitDepth);

    // Analysis buffers
    const timeDomainData = new Float32Array(analyser.fftSize);
    const frequencyData = new Uint8Array(analyser.frequencyBinCount);

    // Analysis loop using requestAnimationFrame for < 500ms latency
    const analyze = () => {
      if (!analyserRef.current) return;

      analyserRef.current.getFloatTimeDomainData(timeDomainData);
      analyserRef.current.getByteFrequencyData(frequencyData);

      // --- Noise Level (RMS over sliding 2s window) ---
      const buffer = noiseBufferRef.current;
      let idx = noiseBufferIndexRef.current;
      const framesToCopy = Math.min(timeDomainData.length, buffer.length);
      for (let i = 0; i < framesToCopy; i++) {
        buffer[idx % buffer.length] = timeDomainData[i];
        idx++;
      }
      noiseBufferIndexRef.current = idx;

      // Calculate noise floor over the filled portion of the buffer
      const filledLength = Math.min(idx, buffer.length);
      const filledBuffer = buffer.subarray(0, filledLength);
      const rms = calculateNoiseFloor(filledBuffer, currentSampleRate);
      const dbfs = rmsToDbfs(rms);
      const noiseLevel = evaluateNoiseLevel(dbfs);

      // --- Multi-Speaker Detection (spectral centroid variance) ---
      const centroid = calculateSpectralCentroid(
        frequencyData,
        currentSampleRate,
        analyser.fftSize
      );
      centroidHistoryRef.current.push(centroid);
      if (centroidHistoryRef.current.length > maxCentroidHistory) {
        centroidHistoryRef.current.shift();
      }

      let centroidVariance = 0;
      if (centroidHistoryRef.current.length > 2) {
        const history = centroidHistoryRef.current;
        const mean = history.reduce((a, b) => a + b, 0) / history.length;
        centroidVariance =
          history.reduce((sum, val) => sum + (val - mean) ** 2, 0) / history.length;
      }
      const multiSpeakerLevel = evaluateMultiSpeaker(centroidVariance);

      // --- Speech Activity Detection ---
      totalFramesRef.current++;
      // Check if current frame has speech (amplitude above threshold)
      let maxAmplitude = 0;
      for (let i = 0; i < timeDomainData.length; i++) {
        const abs = Math.abs(timeDomainData[i]);
        if (abs > maxAmplitude) maxAmplitude = abs;
      }
      if (maxAmplitude > SPEECH_AMPLITUDE_THRESHOLD) {
        speechFramesRef.current++;
      }
      const activityRatio =
        totalFramesRef.current > 0
          ? speechFramesRef.current / totalFramesRef.current
          : 0;
      const speechActivityLevel = evaluateSpeechActivity(activityRatio);

      // --- Compose metrics ---
      const currentMetrics = {
        noise: noiseLevel,
        multiSpeaker: multiSpeakerLevel,
        bitDepth: bitDepthLevel,
        sampleRate: sampleRateLevel,
        speechActivity: speechActivityLevel,
      };

      const overallStatus = computeOverallStatus(currentMetrics);

      setMetrics({ ...currentMetrics, overallStatus });

      animationFrameRef.current = requestAnimationFrame(analyze);
    };

    // Start analysis loop
    animationFrameRef.current = requestAnimationFrame(analyze);

    return cleanup;
  }, [stream, cleanup]);

  return metrics;
}

// --- Component ---

interface QualityAnalyzerProps {
  stream: MediaStream | null;
}

const STATUS_COLORS: Record<QualityStatus, string> = {
  green: 'bg-green-500',
  yellow: 'bg-yellow-400',
  red: 'bg-red-500',
};

const STATUS_RING_COLORS: Record<QualityStatus, string> = {
  green: 'ring-green-200',
  yellow: 'ring-yellow-200',
  red: 'ring-red-200',
};

const METRIC_LABELS: Record<string, string> = {
  noise: 'Noise',
  multiSpeaker: 'Live Overlap Check',
  bitDepth: 'Bit Depth',
  sampleRate: 'Sample Rate',
  speechActivity: 'Speech Activity',
};

/**
 * QualityAnalyzer — Renders color-coded quality indicator badges.
 *
 * Integrates with the useQualityAnalyzer hook to display real-time
 * audio quality metrics as visual indicators (green/yellow/red circles
 * with metric names and tooltips).
 */
export function QualityAnalyzer({ stream }: QualityAnalyzerProps) {
  const metrics = useQualityAnalyzer(stream);

  const metricEntries: Array<{ key: string; level: QualityLevel }> = [
    { key: 'noise', level: metrics.noise },
    { key: 'multiSpeaker', level: metrics.multiSpeaker },
    { key: 'bitDepth', level: metrics.bitDepth },
    { key: 'sampleRate', level: metrics.sampleRate },
    { key: 'speechActivity', level: metrics.speechActivity },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div
          className={`h-3 w-3 rounded-full ${STATUS_COLORS[metrics.overallStatus]}`}
          aria-hidden="true"
        />
        <span className="text-sm font-medium text-gray-700">
          Overall Quality:{' '}
          <span
            className={
              metrics.overallStatus === 'green'
                ? 'text-green-700'
                : metrics.overallStatus === 'yellow'
                  ? 'text-yellow-700'
                  : 'text-red-700'
            }
          >
            {metrics.overallStatus === 'green'
              ? 'Good'
              : metrics.overallStatus === 'yellow'
                ? 'Marginal'
                : 'Failing'}
          </span>
        </span>
      </div>

      <div className="flex flex-wrap gap-2" role="status" aria-label="Audio quality indicators">
        {metricEntries.map(({ key, level }) => (
          <QualityBadge
            key={key}
            label={METRIC_LABELS[key]}
            level={level}
          />
        ))}
      </div>
    </div>
  );
}

// --- Sub-components ---

interface QualityBadgeProps {
  label: string;
  level: QualityLevel;
}

function QualityBadge({ label, level }: QualityBadgeProps) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${STATUS_RING_COLORS[level.status]} bg-white`}
      title={level.message}
      role="status"
      aria-label={`${label}: ${level.status}`}
    >
      <span
        className={`h-2 w-2 rounded-full ${STATUS_COLORS[level.status]}`}
        aria-hidden="true"
      />
      <span className="text-gray-700">{label}</span>
    </div>
  );
}
